// Accès confidentiel — sections/access-gate.liquid
if (!customElements.get('access-gate')) {
  const sha256 = async (value) => {
    const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(buffer)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  customElements.define('access-gate', class AccessGate extends HTMLElement {
    connectedCallback() {
      this.hashes = (this.dataset.codes || '').split(',').filter(Boolean);
      this.end = Number(this.dataset.end) * 1000;
      this.form = this.querySelector('.access-gate__form');
      this.input = this.querySelector('.access-gate__input');
      this.error = this.querySelector('.access-gate__error');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));

      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.selectTab(tab)));
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
      if (this.input) this.input.addEventListener('input', () => this.setError(false));

      this.subscribeForm = this.querySelector('.access-gate__subscribe');
      if (this.subscribeForm) this.subscribeForm.addEventListener('submit', (event) => this.onSubscribe(event));

      if (this.end > 0) {
        this.digits = {};
        this.querySelectorAll('[data-unit]').forEach((el) => { this.digits[el.dataset.unit] = el; });
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    selectTab(tab) {
      this.tabs.forEach((other) => {
        const selected = other === tab;
        other.setAttribute('aria-selected', selected);
        other.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(other.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
    }

    async onSubmit(event) {
      event.preventDefault();
      const value = this.input.value.trim().toUpperCase();
      if (!value) {
        this.setError(true);
        return;
      }
      const button = this.form.querySelector('[type="submit"]');
      button.setAttribute('aria-busy', 'true');
      try {
        const hash = await sha256(value);
        if (this.hashes.includes(hash)) {
          try { localStorage.setItem(this.dataset.storageKey, '1'); } catch (error) {}
          this.unlock();
        } else {
          this.setError(true);
        }
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    async onSubscribe(event) {
      event.preventDefault();
      const form = this.subscribeForm;
      const email = form.querySelector('[name="email"]');
      const error = form.querySelector('.access-gate__error');
      const button = form.querySelector('[type="submit"]');
      const value = email.value.trim().toLowerCase();
      const nameInput = form.querySelector('[name="first_name"]');
      const firstName = nameInput ? nameInput.value.trim() : '';
      const showError = (visible) => {
        error.hidden = !visible;
        email.setAttribute('aria-invalid', visible ? 'true' : 'false');
      };

      button.setAttribute('aria-busy', 'true');
      try {
        if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(value) || !(await this.domainAcceptsEmail(value.split('@')[1]))) {
          showError(true);
          return;
        }
        showError(false);

        const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(form.dataset.companyId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: `Accès confidentiel — ${document.title}`,
                profile: { data: { type: 'profile', attributes: firstName ? { email: value, first_name: firstName } : { email: value } } }
              },
              relationships: { list: { data: { type: 'list', id: form.dataset.listId } } }
            }
          })
        });
        if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
        this.showSuccess(value, firstName);
      } catch (err) {
        console.error('[access-gate]', err);
        showError(true);
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    async domainAcceptsEmail(domain) {
      try {
        const response = await fetch(`https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(domain)}&type=MX`, {
          headers: { Accept: 'application/dns-json' }
        });
        if (!response.ok) return true;
        const data = await response.json();
        return data.Status === 0 && Array.isArray(data.Answer) && data.Answer.length > 0;
      } catch (error) {
        return true;
      }
    }

    showSuccess(email, firstName = '') {
      this.subscribeForm.hidden = true;
      const success = this.querySelector('.access-gate__success');
      if (!success) return;
      success.querySelectorAll('[data-success-email]').forEach((el) => { el.textContent = email; });
      success.querySelectorAll('[data-success-name]').forEach((el) => { el.textContent = firstName; });
      const codeEl = success.querySelector('[data-reveal-code]');
      const code = codeEl ? atob(codeEl.dataset.revealCode) : '';
      if (codeEl) codeEl.textContent = code;
      const openButton = success.querySelector('[data-open-with-code]');
      if (openButton && code) {
        openButton.addEventListener('click', () => {
          this.input.value = code;
          this.form.requestSubmit();
        });
      }
      success.hidden = false;
    }

    setError(visible) {
      if (this.error) this.error.hidden = !visible;
      if (this.input) this.input.setAttribute('aria-invalid', visible ? 'true' : 'false');
    }

    unlock() {
      clearInterval(this.timer);
      document.documentElement.classList.add('access-gate-open');
      window.scrollTo(0, 0);
      window.dispatchEvent(new Event('resize'));
    }

    tick() {
      const remaining = Math.max(0, this.end - Date.now());
      if (remaining === 0) {
        this.unlock();
        return;
      }
      const seconds = Math.floor(remaining / 1000);
      const values = {
        days: Math.floor(seconds / 86400),
        hours: Math.floor((seconds % 86400) / 3600),
        minutes: Math.floor((seconds % 3600) / 60),
        seconds: seconds % 60
      };
      Object.keys(values).forEach((unit) => {
        if (this.digits[unit]) this.digits[unit].textContent = String(values[unit]).padStart(2, '0');
      });
    }
  });
}
